import { useConfig } from '@exam-management/configuration';
import {
  type GetQBankStructureLevelItemsByQBankStructureIdQuery,
  type GetQBankStructureLevelsByQBankStructureIdQuery,
  QBankStructureLevelCategory,
  QuestionGroupType,
  QuestionStatus,
  useGetQBankStructureLevelItemsByQBankStructureIdQuery,
  useGetQBankStructureLevelsByQBankStructureIdQuery,
  useSearchQuestionsForQuestionReviewTaskQuery,
} from '@exam-management/graphql';
import { Flex, SectionTitle, Text } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useEffect, useMemo } from 'react';

import { AddQuestionButton } from '../../components';
import {
  TaskManagementQuestionListDataGrid,
  type TaskManagementQuestionListType,
} from '../../components/task-management-question-list-data-grid';
import {
  useQuestionReviewTaskCreationFormContext,
  useQuestionReviewTaskCreationSelectedRowsContext,
} from '../../providers';
import { AddQuestionModal } from '../add-question-modal';

import {
  createSelectedQuestionRows,
  removeItemSetQuestionIds,
  removeQuestionById,
} from './question-row-mappers';

function getCourseLevelId(
  qbsLevels?: GetQBankStructureLevelsByQBankStructureIdQuery,
  category?: QBankStructureLevelCategory,
) {
  return qbsLevels?.qbsGetQBankStructureLevelsByQBankStructureId.find(
    (level) => level.category === category,
  )?.id;
}

function getLevelItemIdByLevelId(
  qbsLevelItems?: GetQBankStructureLevelItemsByQBankStructureIdQuery,
  qBankStructureLevelId?: string,
) {
  return qbsLevelItems?.qbsGetQBankStructureLevelItemsByQBankStructureId.find(
    (item) => item.qBankStructureLevelId === qBankStructureLevelId,
  )?.id;
}

function getChapterName(
  qbsLevels?: GetQBankStructureLevelsByQBankStructureIdQuery,
  qbsLevelItems?: GetQBankStructureLevelItemsByQBankStructureIdQuery,
) {
  const chapterLevelId = getCourseLevelId(qbsLevels, QBankStructureLevelCategory.Chapter);

  return (
    qbsLevelItems?.qbsGetQBankStructureLevelItemsByQBankStructureId.find(
      (item) => item.qBankStructureLevelId === chapterLevelId,
    )?.tag?.name ?? 'ไม่ระบุ'
  );
}

function createQuestionDetailUrl(row: TaskManagementQuestionListType) {
  if (!row.qBankStructureId) {
    return null;
  }

  if (row.questionGroupId) {
    return `/qbank-structures/${row.qBankStructureId}/qcontents/item-set/${row.questionGroupId}/${row.id}`;
  }

  return `/qbank-structures/${row.qBankStructureId}/qcontents/single/${row.id}`;
}

export default function AddQuestionSection() {
  const [isAddQuestionModalOpen, { open: addQuestionModalOpen, close: addQuestionModalClose }] =
    useDisclosure(false);

  const configuration = useConfig();
  const internalUserEmailDomains = configuration.modules.userManagement.internalUserEmailDomains;

  const formContext = useQuestionReviewTaskCreationFormContext();
  const { setSelectedQuestionRows } = useQuestionReviewTaskCreationSelectedRowsContext();
  const courseId = formContext.watch('courseId')?.value ?? '';
  const selectedQuestionMap = formContext.watch('question')?.questionId ?? {};
  const selectedQuestionIds = useMemo(
    () =>
      Object.entries(selectedQuestionMap)
        .filter(([, isSelected]) => Boolean(isSelected))
        .map(([questionId]) => questionId),
    [selectedQuestionMap],
  );

  const { data: qbsLevelItems } = useGetQBankStructureLevelItemsByQBankStructureIdQuery({
    variables: {
      qBankStructureId: courseId,
      options: null,
    },
    skip: !courseId,
  });

  const { data: qbsLevels } = useGetQBankStructureLevelsByQBankStructureIdQuery({
    variables: {
      qBankStructureId: courseId,
    },
    skip: !courseId,
  });

  const courseLevelId = getCourseLevelId(qbsLevels, QBankStructureLevelCategory.Course);
  const levelItemId = getLevelItemIdByLevelId(qbsLevelItems, courseLevelId);
  const chapterName = getChapterName(qbsLevels, qbsLevelItems);

  const { data: searchQuestionsResult } = useSearchQuestionsForQuestionReviewTaskQuery({
    variables: {
      search: {
        refQBankStructureId: courseId,
        levelItemId,
        status: [QuestionStatus.PreDraft],
        questionGroupType: [QuestionGroupType.Single, QuestionGroupType.ItemSet],
      },
      paging: {
        currentPage: 0,
        pageSize: 9999,
      },
    },
    skip: !courseId || !levelItemId,
  });

  const selectedQuestions = useMemo(
    () =>
      (searchQuestionsResult?.qctSearchQuestions.questions ?? []).filter((question) =>
        selectedQuestionIds.includes(question.id),
      ),
    [searchQuestionsResult, selectedQuestionIds],
  );

  const rows = useMemo(
    () =>
      createSelectedQuestionRows({
        questions: selectedQuestions,
        chapterName,
        internalUserEmailDomains,
      }),
    [chapterName, internalUserEmailDomains, selectedQuestions],
  );

  useEffect(() => {
    setSelectedQuestionRows(rows);
  }, [rows, setSelectedQuestionRows]);

  const handleRemoveQuestion = (row: TaskManagementQuestionListType) => {
    const nextQuestionIdMap = removeQuestionById(selectedQuestionMap, row.id);

    formContext.setValue('question', { questionId: nextQuestionIdMap });
  };

  const handleRemoveItemSet = (row: TaskManagementQuestionListType) => {
    const nextQuestionIdMap = removeItemSetQuestionIds(
      selectedQuestionMap,
      row.questionIds ?? [row.id],
    );

    formContext.setValue('question', { questionId: nextQuestionIdMap });
  };

  const handleRowClick = (row: TaskManagementQuestionListType) => {
    const url = createQuestionDetailUrl(row);

    if (!url) {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(4),
        })}
      >
        <SectionTitle title='2. เลือกคำถามที่ต้องการให้ทบทวน' />
        <Flex
          sx={{
            flexDirection: 'column',
          }}
        >
          <Flex
            sx={(theme) => ({
              justifyContent: 'space-between',
              px: theme.spacing(6),
              py: theme.spacing(4),
              border: `1px solid ${theme.palette.grey[200]}`,
              borderBottom: 0,
              borderTopLeftRadius: theme.shape.rounded(2),
              borderTopRightRadius: theme.shape.rounded(2),
            })}
          >
            <Text variant='h3' fontWeight='SemiBold'>
              Question List
            </Text>
            <AddQuestionButton onClick={addQuestionModalOpen} disabled={!courseId} />
          </Flex>
          <TaskManagementQuestionListDataGrid
            rows={rows}
            onRemoveQuestion={handleRemoveQuestion}
            onRemoveItemSet={handleRemoveItemSet}
            onRowClick={handleRowClick}
          />
        </Flex>
      </Flex>
      <AddQuestionModal
        open={isAddQuestionModalOpen}
        onClose={addQuestionModalClose}
        levelItemId={levelItemId ?? ''}
        qbsLevelItem={qbsLevelItems as GetQBankStructureLevelItemsByQBankStructureIdQuery}
        qbsLevels={qbsLevels as GetQBankStructureLevelsByQBankStructureIdQuery}
      />
    </>
  );
}
